import { useState } from 'react';
import { Box, Chip, IconButton, Link as MuiLink, Typography } from '@mui/material';
import { Business, Delete, Folder } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_ACCESSES } from '../user.graphql';
import { GET_ACCESSES_FOR_USER } from '../../pdf/pdf.graphql';
import { RemoveUserAccessDialog } from '../../licenses/details/RemoveUserAccessDialog';
import { UserAccessHistoric } from './UserAccessHistoric';
import { formatDateOnly } from '../../Formatters';
import { License, UserAccess } from '../interfaces';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Props {
  userId: number;
}

interface SingleDoc {
  id: number;
  docName: string;
  title: string;
  validFrom: string;
  validTo: string;
}

export function AccessesPanel({ userId }: Props) {
  const accesses = useQuery<{ userAccess: UserAccess[] }>(GET_ACCESSES, { variables: { id: userId } });
  const singleDocs = useQuery<{ accesses: SingleDoc[] }>(GET_ACCESSES_FOR_USER, { variables: { userId } });
  const [toRemove, setToRemove] = useState<UserAccess | undefined>(undefined);

  if (accesses.error) return <Box sx={{ p: 3 }}><span>Feil! {accesses.error.message}</span></Box>;

  const grouped = accesses.data ? groupByLicense(accesses.data.userAccess) : [];
  const docs = singleDocs.data?.accesses ?? [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {toRemove && (
        <RemoveUserAccessDialog
          displayName={toRemove.subscription.name}
          userId={userId}
          userAccessId={toRemove.id}
          licenseId={toRemove.license.id}
          onClose={() => setToRemove(undefined)}
        />
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Aktive abonnementstilganger gruppert per lisens
      </Typography>

      {accesses.loading && !accesses.data && <LoadingSpinner />}

      {!accesses.loading && grouped.length === 0 && docs.length === 0 && (
        <Typography variant="caption" color="textDisabled">Har ingen tilganger</Typography>
      )}

      {grouped.map((group) => (
        <Box
          key={group.license.id}
          sx={{
            mb: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              bgcolor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Business fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">
                gjennom
              </Typography>
              <MuiLink
                component={Link}
                to={`/license/${group.license.id}`}
                variant="body2"
                underline="hover"
                sx={{ fontWeight: 'fontWeightMedium' }}>
                {group.license.companyName || '<Mangler navn>'}
              </MuiLink>
            </Box>
            <Chip size="small" label={group.accessList.length === 1 ? '1 tilgang' : `${group.accessList.length} tilganger`} />
          </Box>
          <Box>
            {group.accessList.map((item, i) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  px: 2,
                  py: 1.5,
                  borderTop: i === 0 ? 0 : 1,
                  borderColor: 'divider',
                }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                    {item.subscription.name}
                  </Typography>
                  <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25 }}>
                    fra {formatDateOnly(item.createdAt)}
                  </Typography>
                </Box>
                <IconButton size="small" title="Fjern tilgang" onClick={() => setToRemove(item)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      {docs.length > 0 && (
        <>
          <Typography variant="overline" component="div" color="text.secondary" sx={{ mt: 3, mb: 1, lineHeight: 1.5 }}>
            Enkeltdokument-tilganger
          </Typography>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {docs.map((d, i) => (
              <Box
                key={d.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  px: 2,
                  py: 1.5,
                  borderTop: i === 0 ? 0 : 1,
                  borderColor: 'divider',
                }}>
                <Folder fontSize="small" color="action" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                      {d.docName}
                    </Box>
                    {d.title}
                  </Typography>
                  <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25 }}>
                    fra {formatDateOnly(d.validFrom)} til {formatDateOnly(d.validTo)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <UserAccessHistoric userId={userId} />
      </Box>
    </Box>
  );
}

function groupByLicense(accessList: UserAccess[]): { license: License; accessList: UserAccess[] }[] {
  const grouped = accessList
    .filter((x) => !x.isDeleted)
    .reduce((g: Record<number, UserAccess[]>, item) => {
      g[item.license.id] = g[item.license.id] || [];
      g[item.license.id].push(item);
      return g;
    }, {});
  return Object.values(grouped).map((list) => ({ license: list[0].license, accessList: list }));
}
